'use client';

import React, { Suspense } from 'react';
import { VStack, Text, Box } from '@skin-seoul/design-system';
import { useUser, useOrderService, transformOrderForDisplay } from '@skin-seoul/service';
import { OrderCard, OrderCardSkeleton, LucideIcon } from '@skin-seoul/shared-components';
import { ErrorBoundary } from '@skin-seoul/utils';

// Fallback component that silently skips failed orders as we get 404 for failed orders
const EmptyFallback = () => null;

const OrderItem = ({ orderId }: { orderId: number }) => {
  const { useGetOrder } = useOrderService();
  const { data: order } = useGetOrder({ orderId });

  if (!order) {
    return null;
  }

  const transformedOrder = transformOrderForDisplay(order);

  return <OrderCard order={transformedOrder} />;
};

export const RegisteredDeliveryTracking = () => {
  const { user, isLoading: isUserLoading, isAuthenticated } = useUser();
  const { useGetUserOrders } = useOrderService();

  const {
    data: userOrdersData,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
  } = useGetUserOrders({
    userId: user?.id || 0,
  });

  if (isUserLoading || isOrdersLoading) {
    return (
      <VStack width="100%" gap={4} padding={4}>
        <OrderCardSkeleton />
        <OrderCardSkeleton />
        <OrderCardSkeleton />
      </VStack>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <VStack
        width="100%"
        bg="generic.0"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={8}
        gap={4}
        alignItems="center"
        justifyContent="center"
        minHeight="300px"
      >
        <VStack gap={3} alignItems="center">
          <LucideIcon name="User" width={16} height={16} color="neutral.400" />
          <Text textStyle="heading.h5.mobile" fontWeight="semibold" color="neutral.700" textAlign="center">
            Please Log In
          </Text>
          <Text
            textStyle="paragraph.medium"
            fontWeight="regular"
            color="neutral.500"
            textAlign="center"
            maxWidth="400px"
          >
            You need to be logged in to view your order tracking information. Please log in to continue.
          </Text>
        </VStack>
      </VStack>
    );
  }

  if (isOrdersError) {
    return (
      <VStack
        width="100%"
        bg="generic.0"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={8}
        gap={4}
        alignItems="center"
        justifyContent="center"
        minHeight="300px"
      >
        <VStack gap={3} alignItems="center">
          <LucideIcon name="CircleAlert" width={16} height={16} color="error.500" />
          <Text textStyle="heading.h5.mobile" fontWeight="semibold" color="neutral.700" textAlign="center">
            Unable to Load Orders
          </Text>
          <Text
            textStyle="paragraph.medium"
            fontWeight="regular"
            color="neutral.500"
            textAlign="center"
            maxWidth="400px"
          >
            We&apos;re having trouble loading your orders. Please try again later.
          </Text>
        </VStack>
      </VStack>
    );
  }

  if (!userOrdersData?.orderIds || userOrdersData.orderIds.length === 0) {
    return (
      <VStack
        width="100%"
        bg="generic.0"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={8}
        gap={4}
        alignItems="center"
        justifyContent="center"
        minHeight="300px"
      >
        <VStack gap={3} alignItems="center">
          <LucideIcon name="Box" width={16} height={16} color="neutral.400" />
          <Text textStyle="heading.h5.mobile" fontWeight="semibold" color="neutral.700" textAlign="center">
            No Orders Found
          </Text>
          <Text
            textStyle="paragraph.medium"
            fontWeight="regular"
            color="neutral.500"
            textAlign="center"
            maxWidth="400px"
          >
            You haven&apos;t placed any orders yet. Start shopping to see your orders here!
          </Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack width="100%" gap={4} padding={4}>
      <Box width="100%">
        <Text textStyle="heading.h4.mobile" fontWeight="bold" color="neutral.700" marginBottom={2}>
          Your Orders
        </Text>
        <Text textStyle="paragraph.medium" fontWeight="regular" color="neutral.500">
          Track your order deliveries and view order details
        </Text>
      </Box>

      <VStack width="100%" gap={4}>
        {userOrdersData.orderIds.map((orderId) => (
          <ErrorBoundary key={orderId} fallback={<EmptyFallback />}>
            <Suspense fallback={<OrderCardSkeleton />}>
              <OrderItem orderId={orderId} />
            </Suspense>
          </ErrorBoundary>
        ))}
      </VStack>
    </VStack>
  );
};
